import React from 'react';
import {
  DOMAIN_OPTIONS,
  STATUS_OPTIONS,
  SEVERITY_GROUP_OPTIONS
} from '../../lib/riskConstants';

/**
 * Modern Filter Bar with Search, Dropdown Selectors, Active Filter Chips and View Toggle
 */
export default function RiskFilterBar({
  searchQuery = '',
  setSearchQuery,
  filterDomain = 'all',
  setFilterDomain,
  filterStatus = 'all',
  setFilterStatus,
  filterSeverityGroup = 'all',
  setFilterSeverityGroup,
  filterLocation = 'all',
  setFilterLocation,
  uniqueLocations = [],
  activeView = 'kanban',
  setActiveView,
  activeFiltersCount = 0,
  clearFilters,
  isMobile = false
}) {
  const hasActiveFilters = activeFiltersCount > 0;

  // Helpers for chip labels
  const getDomainLabel = (val) => DOMAIN_OPTIONS.find((o) => o.value === val)?.label || val;
  const getStatusLabel = (val) => STATUS_OPTIONS.find((o) => o.value === val)?.label || val;
  const getSeverityLabel = (val) => SEVERITY_GROUP_OPTIONS.find((o) => o.value === val)?.label || val;

  return (
    <div className="risk-filter-bar" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '14px' }}>
      {/* Top Row: Search & Filters & View Mode Switch */}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '12px',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          width: '100%'
        }}
      >
        {/* Left Inputs */}
        <div className="risk-filter-inputs">
          {/* Search Box */}
          <div className="risk-search-wrapper">
            <span className="risk-search-icon">🔍</span>
            <input
              type="text"
              className="risk-search-input"
              placeholder="ค้นหาหัวข้อ, HN, เลขเคส, สถานที่..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="risk-search-clear"
                onClick={() => setSearchQuery('')}
                title="ล้างคำค้นหา"
              >
                ✕
              </button>
            )}
          </div>

          {/* Domain Filter */}
          <select
            className="risk-select"
            value={filterDomain}
            onChange={(e) => setFilterDomain(e.target.value)}
            aria-label="กรองประเภทความเสี่ยง"
          >
            {DOMAIN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            className="risk-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            aria-label="กรองสถานะ"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Severity Group Filter */}
          <select
            className="risk-select"
            value={filterSeverityGroup}
            onChange={(e) => setFilterSeverityGroup(e.target.value)}
            aria-label="กรองระดับความรุนแรง"
          >
            {SEVERITY_GROUP_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Location Filter */}
          <select
            className="risk-select"
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value)}
            aria-label="กรองสถานที่"
          >
            <option value="all">ทุกสถานที่</option>
            {uniqueLocations.map((loc, idx) => (
              <option key={idx} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </div>

        {/* Right: View Mode Toggle */}
        <div className="risk-view-toggle">
          <button
            type="button"
            className={`risk-view-toggle-btn ${activeView === 'kanban' ? 'risk-view-toggle-btn--active' : ''}`}
            onClick={() => setActiveView('kanban')}
          >
            <span>📋</span>
            <span>Kanban Board</span>
          </button>
          <button
            type="button"
            className={`risk-view-toggle-btn ${activeView === 'table' ? 'risk-view-toggle-btn--active' : ''}`}
            onClick={() => setActiveView('table')}
          >
            <span>📑</span>
            <span>ตารางข้อมูล</span>
          </button>
        </div>
      </div>

      {/* Bottom Row: Active Filter Tag Chips */}
      {hasActiveFilters && (
        <div className="risk-filter-chips">
          <span style={{ fontSize: '0.78em', color: '#64748b', fontWeight: '700' }}>
            ตัวกรองที่ใช้งาน ({activeFiltersCount}):
          </span>

          {searchQuery.trim() && (
            <span className="risk-filter-chip">
              <span>ค้นหา: "{searchQuery}"</span>
              <button
                type="button"
                className="risk-filter-chip-remove"
                onClick={() => setSearchQuery('')}
                title="ลบตัวกรองนี้"
              >
                ✕
              </button>
            </span>
          )}

          {filterDomain !== 'all' && (
            <span className="risk-filter-chip">
              <span>ประเภท: {getDomainLabel(filterDomain)}</span>
              <button
                type="button"
                className="risk-filter-chip-remove"
                onClick={() => setFilterDomain('all')}
                title="ลบตัวกรองนี้"
              >
                ✕
              </button>
            </span>
          )}

          {filterStatus !== 'all' && (
            <span className="risk-filter-chip">
              <span>สถานะ: {getStatusLabel(filterStatus)}</span>
              <button
                type="button"
                className="risk-filter-chip-remove"
                onClick={() => setFilterStatus('all')}
                title="ลบตัวกรองนี้"
              >
                ✕
              </button>
            </span>
          )}

          {filterSeverityGroup !== 'all' && (
            <span className="risk-filter-chip">
              <span>ความรุนแรง: {getSeverityLabel(filterSeverityGroup)}</span>
              <button
                type="button"
                className="risk-filter-chip-remove"
                onClick={() => setFilterSeverityGroup('all')}
                title="ลบตัวกรองนี้"
              >
                ✕
              </button>
            </span>
          )}

          {filterLocation !== 'all' && (
            <span className="risk-filter-chip">
              <span>สถานที่: {filterLocation}</span>
              <button
                type="button"
                className="risk-filter-chip-remove"
                onClick={() => setFilterLocation('all')}
                title="ลบตัวกรองนี้"
              >
                ✕
              </button>
            </span>
          )}

          <button
            type="button"
            className="risk-filter-clear-all"
            onClick={clearFilters}
          >
            ล้างตัวกรองทั้งหมด
          </button>
        </div>
      )}
    </div>
  );
}
